import React, { useMemo, useRef, useState } from "react";
import { soulManager } from "../../data/managers/SoulManager";
import {
  SoulDataModel,
  UpdateSoulInfoPayload,
} from "../../data/models/Soul/SoulDataModel";
import { useAuth } from "../../data/providers/AuthProvider";
import { useUserData } from "../../data/providers/UserDataProvider";

type ProfileForm = {
  fullName: string;
  documentType?: string;
  document?: string;
  address?: string;
  emailOptional?: string;
  birthDate?: string;
};

type FormErrors = Partial<Record<keyof ProfileForm, string>>;

const getInitials = (name?: string) =>
  (name || "Anónimo")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);

const isFutureDate = (iso?: string) => {
  if (!iso) return false;
  const d = new Date(iso);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return d.getTime() > today.getTime();
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const onlyDigits = (v?: string) => (v || "").replace(/\D/g, "");

function validateDocumentByType(
  type?: string,
  value?: string
): string | undefined {
  if (!value) return undefined;
  const digits = onlyDigits(value);
  if (type === "CC" || type === "TI") {
    if (digits.length < 6 || digits.length > 12) return "Documento inválido";
  } else if (type === "CE") {
    if (digits.length < 5 || digits.length > 12) return "Documento inválido";
  } else if (type === "NIT") {
    if (digits.length < 8 || digits.length > 15) return "NIT inválido";
  } else if (type === "PAS") {
    if (value.length < 5 || value.length > 15) return "Pasaporte inválido";
  }
  return undefined;
}

function validateForm(f: ProfileForm): FormErrors {
  const errors: FormErrors = {};

  if ((f.fullName || "").trim().length === 0) {
    errors.fullName = "Ingresa tu nombre o deja Anónimo";
  } else if (f.fullName.trim().length < 2) {
    errors.fullName = "Debe tener al menos 2 caracteres";
  } else if (f.fullName.length > 80) {
    errors.fullName = "Máximo 80 caracteres";
  }

  const docErr = validateDocumentByType(f.documentType, f.document);
  if (docErr) errors.document = docErr;

  if (f.address && f.address.length > 120) {
    errors.address = "Máximo 120 caracteres";
  }

  if (f.emailOptional && !emailRegex.test(f.emailOptional)) {
    errors.emailOptional = "Correo inválido";
  } else if (f.emailOptional && f.emailOptional.length > 120) {
    errors.emailOptional = "Máximo 120 caracteres";
  }

  if (isFutureDate(f.birthDate)) {
    errors.birthDate = "La fecha no puede ser futura";
  }

  return errors;
}

export function useProfile() {
  const userData = useUserData()!;
  const { user } = useAuth();

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialForm = useMemo<ProfileForm>(() => {
    return {
      fullName: userData?.data?.fullName?.trim() || "Anónimo",
      documentType: userData?.data?.documentType || "",
      document: userData?.data?.passport || "",
      address: userData?.data?.address || "",
      emailOptional: userData?.data?.email || "",
      birthDate: userData?.data?.birthDate || "",
    };
  }, [userData]);

  const [form, setForm] = useState<ProfileForm>(initialForm);

  React.useEffect(() => {
    setForm(initialForm);
    setErrors({});
  }, [initialForm]);

  const handleChange =
    (field: keyof ProfileForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleBlur =
    (field: keyof ProfileForm) => (e: React.FocusEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      const v = validateForm(next);
      setErrors(v);
    };

  const handlePickPhoto = () => fileInputRef.current?.click();

  const handleUploadPhoto = async (file: File) => {
    if (!user?.uid) return;
    setUploading(true);
    try {
      // const avatarRef = ref(storage, `users/${user.uid}/avatar.jpg`);
      // await uploadBytes(avatarRef, file);
      // const url = await getDownloadURL(avatarRef);
      // await updateProfile(user, { photoURL: url });
      // SIMULATED: si tuvieras userData.reloadData() podrías llamarlo aquí
      // await userData.reloadData?.();
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleUploadPhoto(f);
  };

  const handleSave = async () => {
    if (!user?.uid) return;

    const v = validateForm(form);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setSaving(true);
    try {
      const soulData: SoulDataModel = {
        fullName: form.fullName?.trim() || "Anónimo",
        documentType: form.documentType?.trim() || null!,
        passport: form.document?.trim() || null!,
        address: form.address?.trim() || null!,
        email: form.emailOptional?.trim() || null!,
        birthDate: form.birthDate || null!,
      };
      const payload = new UpdateSoulInfoPayload(soulData);
      await soulManager.updateSoulInfo(payload);
      handleReload();
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    if (!user?.uid) return;
    setReloading(true);
    try {
      await userData.reloadUserData();
      setErrors({});
    } finally {
      setReloading(false);
    }
  };

  return {
    user,
    form,
    handlePickPhoto,
    fileInputRef,
    uploading,
    reloading,
    handleChange,
    handleBlur,
    errors,
    setErrors,
    setForm,
    saving,
    handleSave,
    initialForm,
  };
}
